'use client'
import { useEffect, useReducer, useRef, useContext } from 'react'
import Section from 'components/Section'
import { Typography } from 'components/Contentful/Typography'
import { Grid, Pagination } from '@achieve/ascend'
import { Filter } from 'components/Filter'
import { CardPress } from 'components/Cards/CardPress'
import { useMemoizedContentGetter } from 'utils/contentful'
import styles from './PaginatedStoryGrid.module.scss'
import { LayoutContext } from 'providers/LayoutProvider'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { filterReducer } from 'utils/shared/filter'
import { AchieveLink } from 'components/AchieveLink'
import { mainProductConfig } from 'constants/mainProductConfig'

const slugifyTitleForId = (title) => {
  try {
    return title?.content[0]?.content[0]?.value.toLowerCase().split(' ').join('_')
  } catch {
    return 'press-story-grid-title'
  }
}

// Safely parse page number from query params, handling edge cases
const getValidPageNumber = (queryPage) => {
  if (!queryPage) return 1
  // Handle array case (e.g., ?page=2&page=3)
  const pageValue = Array.isArray(queryPage) ? queryPage[0] : queryPage
  const parsed = parseInt(pageValue, 10)
  // Return 1 if NaN, negative, or zero
  return !isNaN(parsed) && parsed > 0 ? parsed : 1
}

const fetchArticles = async ({ dispatch, body, signal }) => {
  try {
    const res = await fetch('/api/contentful/fetch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    })
    const contentfulJson = await res.json()
    if (signal?.aborted) return

    dispatch({
      type: 'SET_ARTICLES',
      payload: { articles: contentfulJson.items, total: contentfulJson.total },
    })
  } catch (error) {
    if (error.name === 'AbortError') return
    dispatch({ type: 'SET_ARTICLES', payload: { articles: [] } })
  }
}

function PaginatedStoryGrid({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const initialRenderRef = useRef(true)
  const { query } = useAppRouter()

  const { state: { viewportSize } = {} } = useContext(LayoutContext)
  const variant = viewportSize

  let memoizedContent = useMemoizedContentGetter(content, [
    'title',
    'listArticles',
    'filter',
    'config',
  ])

  let { title, filter, listArticles, config } = memoizedContent
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const [{ articlesObject, filterBy, keywordsFilter, page }, dispatch] = useReducer(filterReducer, {
    articlesObject: { articles: listArticles.defaultList, total: listArticles.total },
    filterBy: '*',
    keywordsFilter: '',
    page: getValidPageNumber(query?.page),
    titleId: 'press_story_grid',
  })

  useEffect(() => {
    dispatch({
      type: 'SET_TITLE',
      payload: `${slugifyTitleForId(title?.textContent?.json)}`,
    })
  }, [title])

  // Sync page state with URL changes (including back/forward) when we update the URL via history.pushState
  useEffect(() => {
    const syncFromUrl = () => {
      const params = new URLSearchParams(window.location.search)
      const raw = params.get('page')
      const parsed = raw ? Number.parseInt(raw, 10) : NaN
      const urlPage = Number.isFinite(parsed) && parsed > 0 ? parsed : 1
      if (urlPage !== page) {
        dispatch({ type: 'SET_PAGE', payload: urlPage })
      }
    }

    window.addEventListener('popstate', syncFromUrl)
    return () => window.removeEventListener('popstate', syncFromUrl)
  }, [page])

  useEffect(() => {
    // Skip fetch on initial render only if we're on page 1 with no filters
    // (server already provided the data for page 1)
    if (initialRenderRef.current) {
      initialRenderRef.current = false
      // Check URL directly to determine if we should skip
      const urlPage = getValidPageNumber(query?.page)
      // If URL shows page 1 with no filters, use server data
      if (urlPage === 1 && filterBy === '*' && keywordsFilter === '') {
        return
      }
    }

    let body = {
      content_type: config.jsonContent.longFormContentType,
      pageLimit: config.jsonContent.pageLimit,
      page,
    }

    if (filterBy !== '*') {
      body.filter = filterBy
    }

    if (keywordsFilter !== '') {
      body.keywords = keywordsFilter
    }

    // Always fetch when page changes (page is always truthy >= 1)
    if (page) {
      const controller = new AbortController()
      fetchArticles({ dispatch, body, signal: controller.signal })
      return () => controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterBy, keywordsFilter, config])

  const strictTypography = {
    variant:
      title[
        `variant${
          variant.breakpointLetterSize.charAt(0).toUpperCase() +
          variant.breakpointLetterSize.slice(1)
        }`
      ],
    weight:
      title[
        `weight${
          variant.breakpointLetterSize.charAt(0).toUpperCase() +
          variant.breakpointLetterSize.slice(1)
        }`
      ],
  }

  const handleChangePage = (event, targetPage) => {
    dispatch({ type: 'SET_PAGE', payload: targetPage })

    // Update URL instantly using History API instead of router.push
    const params = new URLSearchParams(window.location.search)
    if (targetPage > 1) {
      params.set('page', String(targetPage))
    } else {
      params.delete('page')
    }
    const basePath = window.location.pathname
    const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath

    // Use pushState for instant URL update without navigation
    window.history.pushState({}, '', newUrl)
  }

  return (
    <>
      <Section className={styles['paginated-story-grid-section']} id="press_grid">
        <Typography
          data-testid="press-grid-title"
          id={`${slugifyTitleForId(title?.textContent?.json)}`}
          className={styles['title']}
          content={title?.textContent?.json}
          variant={strictTypography.variant}
          sx={{ margin: 0 }}
          fontWeight={strictTypography.weight}
          component={title.component}
        />
        <Grid className={styles['filter']} data-testid="press-grid-filter-container">
          <Filter
            data-testid="press-grid-filter"
            jsonContent={filter.jsonContent}
            name={filter.name}
            filterBy={filterBy}
            withKeywords={true}
            withUrl={true}
            config={config}
            setFilterBy={(val) => dispatch({ type: 'SET_FILTER_BY', payload: val })}
            setKeywords={(val) => dispatch({ type: 'SET_KEYWORDS', payload: val })}
          />
        </Grid>

        {articlesObject.articles && articlesObject.articles.length > 0 && (
          <>
            <Grid container className={styles['news-card-grid']} data-testid="press-grid-container">
              <Grid container spacing={3}>
                {articlesObject.articles.map((article, idx) => {
                  return (
                    <Grid
                      size={{ xs: 12, md: 6, xl: 4 }}
                      key={idx}
                      data-testid="press-grid-card-container"
                    >
                      <CardPress
                        data-testid="press-grid-card"
                        key={idx}
                        article={article}
                        showPill={true}
                      />
                    </Grid>
                  )
                })}
              </Grid>
            </Grid>
            {parseInt(articlesObject.total) / parseInt(config.jsonContent.pageLimit) > 1 && (
              <Grid item className={styles['pagination']} data-testid="pagination-wrapper">
                <AchieveLink
                  track={{
                    list_name: 'Pagination Event',
                    click_id: `${
                      mainProductConfig[siteEnv || 'achieve']?.['MainProduct']
                    } | Pagination`,
                    click_position: 'Pagination',
                    click_text: 'Pagination',
                    nav_link_section: 'Pagination',
                    track_event: 'ui_click',
                    event_action: 'ui_click',
                    event_type: 'ui_click',
                    sectionVariationObject,
                  }}
                >
                  <Pagination
                    color="primary"
                    className={styles['pagination-items']}
                    data-testid="press-grid-pagination"
                    count={Math.ceil(
                      parseInt(articlesObject.total) / parseInt(config.jsonContent.pageLimit)
                    )}
                    onChange={handleChangePage}
                    page={page ? parseInt(page) : 1}
                  />
                </AchieveLink>
              </Grid>
            )}
          </>
        )}
      </Section>
    </>
  )
}
export { PaginatedStoryGrid }
export default PaginatedStoryGrid
